import clsx from 'clsx';
import React, { useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import Decimal from 'decimal.js';
import { useTranslation } from 'react-i18next';
import { UnitCategoryCode, FlamingoFund } from 'funds-model';
import { getFundColors } from '../theme/constants';
import FundDetailsDialogAccordion from './FundDetailsDialogAccordion';
import { useIsMobile } from '../utils/useIsMobile';
import { usePieChartLegendHover } from '../PieChart/usePieChartLegendHover';
import { Title } from '../Title/Title';
import { PieChart } from '../PieChart/PieChart';
import { Label } from '../Label/Label';

import useStyles from './FundDetailsDialog.style';

export interface FundDetailsDialogWalletChartProps {
  fund: FlamingoFund;
  unitCategoryCode: UnitCategoryCode;
}

const FundDetailsDialogWalletChart: React.FC<FundDetailsDialogWalletChartProps> = ({ fund , unitCategoryCode}) => {
  const theme = useTheme();
  const c = useStyles({});
  const [t] = useTranslation('components');
  const isMobile = useIsMobile();
  const isTabletOrMobile = useIsMobile('md');
  const [arcOnHover, setArcOnHover] = useState({ color: '', fraction: new Decimal(0) });
  const { pieChartRef, handleLegendHover } = usePieChartLegendHover();

  if (!fund.modelWalletStructure || fund.modelWalletStructure.length === 0) {
    return null;
  }

  const fundColors = getFundColors(theme);
  const circleGraphValuesPieChart = [...fund.modelWalletStructure].filter(mls => mls.unitCategory === unitCategoryCode)
                                                          .sort((obj1, obj2) => obj1.seq - obj2.seq)
                                                          .map((item, i) => ({
                                                            y: +((item.share * 100).toFixed(2) || 0),
                                                            name: item.modelWalletAllocation,
                                                            color: fundColors[i % fundColors.length],
                                                          }));

  return (
    <FundDetailsDialogAccordion title={t('fund-details-dialog.model-wallet-structure')} detailsClassName={c.walletCharBox}>
      <div>
        {!isMobile && <Title titleComponent="h3" titleFontWeight="bold" titleVariant='h3' title={t('fund-details-dialog.model-wallet-structure')} />}
        <div className={c.chartBoxWrapper}>
          <PieChart
            ref={pieChartRef}
            hideTooltip
            hideLegend
            data={circleGraphValuesPieChart}
            chartWidth={isTabletOrMobile ? '100%' : 256}
            onPointHover={(color, value) => setArcOnHover({ color, fraction: new Decimal(value) })}
          />
        </div>
        <div className={c.chartLegendWrapper}>
          <div className={c.chartLegendTitle}>
            <Label size="s" color="graphite">{t('fund-details-dialog.model-wallet-structure-labels.share-label')}</Label>
          </div>
          {circleGraphValuesPieChart.map(item => (
            <li
              className={clsx(c.chartLegendItem, arcOnHover.color === item.color && c.chartLegendItemActive)}
              onMouseEnter={() => handleLegendHover(item.name, true)}
              onMouseLeave={() => handleLegendHover(item.name, false)}
              key={item.name}
            >
              <div className={c.chartLegendItemColor} style={{ backgroundColor: item.color }}/>
              <div className={c.chartLegendItemName}>
                {item.name}
              </div>
              <div className={c.chartLegendItemValue}>
                {item.y}%
              </div>
            </li>
          ))}
        </div>
      </div>
    </FundDetailsDialogAccordion>
  );
};

export default FundDetailsDialogWalletChart;
